import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "@entry/navigation";

import { Routes, StorageKey, UserQuery, UserQueryVariables } from "@ctypes";
import { USER } from "@graphql";
import { useErrorBanner } from "@hooks/application";
import { Analytics } from "@services";
import { getMissingFieldRoute } from "@utils";

/**
 * The onboarding machine must never route off a cache snapshot: several
 * step mutations return partial payloads (setUserFinancialProfile returns
 * only an id) and bank links / beneficiaries are root query fields no
 * mutation updates, so a cached read re-asks completed steps. Every
 * navigation therefore refetches the user from the network, persists the
 * snapshot (so a refresh resumes at the right step), then decides.
 */
export const useOnboarding = () => {
  const navigate = useNavigate();
  const { showErrorBanner } = useErrorBanner();

  const [fetchUser] = useLazyQuery<UserQuery, UserQueryVariables>(USER, {
    fetchPolicy: "network-only",
  });

  const getNextScreen = async () => {
    const { data, error } = await fetchUser();

    if (error || !data) {
      throw new Error("Error retrieving onboarding field data");
    }

    Analytics.identify(data.user);
    localStorage.setItem(StorageKey.User, JSON.stringify(data));

    return getMissingFieldRoute(data);
  };

  const goToNextScreen = async (options?: { replace?: boolean; state?: any }) => {
    try {
      const nextScreen = await getNextScreen();

      if (nextScreen) {
        navigate(nextScreen, options);
      } else {
        navigate(Routes.OnboardingComplete, { replace: true });
      }
    } catch {
      showErrorBanner();
    }
  };

  const navigateToNext = (state?: any) => goToNextScreen({ state });

  const resetToNext = (state?: any) => goToNextScreen({ replace: true, state });

  return { navigateToNext, resetToNext, getNextScreen };
};
